// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
import React, { useState, useEffect, useCallback } from "react";
import {
    View,
    Text,
    TouchableOpacity,
    TouchableWithoutFeedback,
} from "react-native";
import { FontAwesome, MaterialCommunityIcons } from "@expo/vector-icons";
import colors from "../../../../config/colors";
import Picker from "./Elements/ComboPicker";
import DataPicker from "./Elements/DataPicker";
import NumberSelector from "./Elements/NumberSelector";
import styles from "../../../../styles/Farmer/NewDigestateWithdrawlStyle";
import {
    insertNewDigestateWithdrawl,
    fetchBathtubsToWithdrawFrom,
} from "../../../../axios/axiosConfig";
import { formatDate } from "../../../../utils/utils";
import AsyncStorage from "@react-native-async-storage/async-storage";

const NewDigestateWithdrawal = ({ title, onShowSnackbar, getTranslation, fetchData }) => {
    const [collapsed, setCollapsed] = useState(true);
    const [depositId, setDepositId] = useState(null);
    const [quantity, setQuantity] = useState(1000);
    const [bathtubs, setBathtubs] = useState(null);
    const [currentDate, setCurrentDate] = useState("");

    const getCurrentDate = () => {
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split("T")[0];
        return formattedDate;
    };
    const logger = require("../../../../axios/loggingConfig");

    async function fetchTubs(role) {
        try {
            const response = await fetchBathtubsToWithdrawFrom(role);
            if (response.status === 200) {
                setBathtubs(response.data);
                setDepositId(response.data[0].id);
            } else {
                console.warn("NewDigestateWithdrawal::fetchBathtubsToWithdrawFrom, response = ", response);
            }
        } catch (error) {
            logger.error("Error on fetchBathtubsToWithdrawFrom() function in NewDigestateWithdrawal.js: " + error);
        }
    }

    useEffect(() => {
        setCurrentDate(getCurrentDate());
        AsyncStorage.getItem("userData")
            .then((userData) => {
                const userDataObject = JSON.parse(userData);
                if (userDataObject && (userDataObject.landingView === "Farmer" || userDataObject.landingView === "SharedDepositManager")) {
                    fetchTubs(userDataObject.landingView);
                }
            })
            .catch((error) => {
                console.error("Error retrieving userData from AsyncStorage: ", error);
            });
    }, []);

    const handleToggleCollapse = () => {
        setCollapsed(!collapsed);
    };

    const handleNumberChange = (value) => {
        setQuantity(value);
    };

    const handleDatePickerChange = useCallback((date) => {
        setCurrentDate(date);
    }, []);

    const handlePickerChange = useCallback((newId) => {
        setDepositId(newId);
    }, []);

    const handleSubmitRequest = async () => {
        const response = await insertNewDigestateWithdrawl(
            currentDate,
            quantity,
            depositId
        );
        if (response.status == 200) {
            onShowSnackbar(
                getTranslation("Nuovo prelievo di digestato inserito correttamente")
            );
            setCollapsed(true);
            fetchData();
        } else {
            setCollapsed(true);
            onShowSnackbar(getTranslation(response.response.data), colors.red);
            logger.error(
                "Error on insertNewDigestateWithdrawl() function in NewDigestateWithdrawal.js",
                { category: "API" }
            );
        }
        return null;
    };

    return (
        <TouchableWithoutFeedback
            onPress={() => collapsed && handleToggleCollapse()}
        >
            <View style={[styles.card, { borderRadius: 16, overflow: "hidden" }]}>
                {collapsed ? (
                    <View style={styles.header}>
                        <Text style={styles.title}>{title}</Text>
                        <View style={styles.iconRow}>
                            <MaterialCommunityIcons
                                name="wall-sconce-round"
                                size={60}
                                color={colors.green}
                                style={styles.iconSpacing}
                            />
                            <MaterialCommunityIcons
                                name="truck"
                                size={60}
                                color={colors.green}
                            />
                        </View>
                    </View>
                ) : (
                    <View
                        style={[
                            styles.content,
                            {
                                backgroundColor: colors.white,
                                borderRadius: 16,
                                shadowColor: colors.white,
                            },
                        ]}
                    >
                        {/* Toggle button to collapse the card */}
                        <TouchableOpacity
                            onPress={handleToggleCollapse}
                            style={styles.collapseButtonTopLeft}
                        >
                            <FontAwesome name="chevron-up" size={24} color={colors.green} />
                        </TouchableOpacity>

                        <View style={styles.rowContainer}>
                            <View style={styles.labelContainer}>
                                <Text style={styles.label}>{getTranslation("Data")}</Text>
                            </View>
                            <View style={styles.inputContainer}>
                                <TouchableWithoutFeedback>
                                    <View>
                                        <DataPicker
                                            date={formatDate(currentDate)}
                                            setCurrentDate={handleDatePickerChange}
                                            hasMax={true}
                                            formatDate={formatDate}
                                        />
                                    </View>
                                </TouchableWithoutFeedback>
                            </View>
                        </View>

                        <View style={styles.rowContainer}>
                            <View style={styles.labelContainer}>
                                <Text style={styles.label}>
                                    {getTranslation("Quantità(L)")}
                                </Text>
                            </View>
                            <View style={styles.inputContainer}>
                                <TouchableWithoutFeedback>
                                    <View>
                                        <NumberSelector
                                            size="big"
                                            onChange={handleNumberChange}
                                            quantity={1000}
                                        />
                                    </View>
                                </TouchableWithoutFeedback>
                            </View>
                        </View>

                        <View style={styles.rowContainer}>
                            <View style={styles.labelContainer}>
                                <Text style={styles.label}>
                                    {getTranslation("Stoccaggio di Digestato")}
                                </Text>
                            </View>
                            <View style={styles.inputContainer}>
                                <TouchableWithoutFeedback>
                                    <View>
                                        <Picker
                                            field={'nameWithInfo'}
                                            items={bathtubs}
                                            plates={false}
                                            filters={true}
                                            value={depositId ?? undefined}
                                            onValueChange={handlePickerChange}
                                        />
                                    </View>
                                </TouchableWithoutFeedback>
                            </View>
                        </View>

                        <View style={styles.buttonContainer}>
                            <TouchableOpacity
                                onPress={handleSubmitRequest}
                                style={[
                                    styles.button,
                                    {
                                        width: "100%",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        backgroundColor: colors.violet,
                                        borderRadius: 16,
                                    },
                                ]}
                            >
                                <Text
                                    style={[styles.buttonText, { flex: 1, textAlign: "center" }]}
                                >
                                    {getTranslation("Inserisci")}
                                </Text>
                                <FontAwesome
                                    name="pencil"
                                    size={24}
                                    color={colors.white}
                                    style={{ marginLeft: 8 }}
                                />
                            </TouchableOpacity>
                        </View>
                    </View>
                )}
            </View>
        </TouchableWithoutFeedback>
    );
};

export default NewDigestateWithdrawal;
